<!-- SelectFieldFilter.vue -->
<!--
  Mini-modal genérico de filtro por campo de texto.
  Usado por: Por título, Por conteúdo, Por tag, Por fonte.

  Props:
    isOpen    — controla visibilidade
    label     — placeholder do input (ex: "Buscar no título")
    modelValue — valor atual do filtro (v-model)

  Emits:
    update:modelValue — novo valor
    apply             — usuário clicou em "Filtrar"
    close             — pedido de fechamento
-->
<script setup>
import { ref, watch } from "vue";
import AnchoredPanel from "@/components/Generics/AnchoredPanel.vue";

const props = defineProps({
  isOpen:     { type: Boolean, default: false },
  label:      { type: String,  default: "Buscar" },
  modelValue: { type: String,  default: "" },
});

const emit = defineEmits(["update:modelValue", "apply", "close"]);

// Ref para o input — permite chamar .focus() programaticamente
const inputRef = ref(null);

// Cópia local — só propaga no Filtrar
const localValue = ref(props.modelValue);

// Sincroniza quando o pai mudar (ex: reset externo)
watch(
  () => props.modelValue,
  (val) => { localValue.value = val; }
);

// Sincroniza quando abre o modal; o foco vai no @opened do painel, quando o
// campo já existe e está posicionado
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) localValue.value = props.modelValue;
  }
);

function applyFilter() {
  emit("update:modelValue", localValue.value);
  emit("apply");
}

function clearFilter() {
  localValue.value = "";
  emit("update:modelValue", "");
}

// Permite confirmar com Enter
function onKeydown(e) {
  if (e.key === "Enter") applyFilter();
  if (e.key === "Escape") emit("close");
}
</script>

<template>
  <AnchoredPanel
    :open="isOpen"
    placement="side"
    align="left"
    :gap="8"
    @close="emit('close')"
    @opened="inputRef?.focus()"
  >
    <div
      class="field-filter-container"
      role="dialog"
      :aria-label="`Filtrar: ${label}`"
      @click.stop
      @mousedown.stop
    >
      <div class="input-section">
        <input
          ref="inputRef"
          v-model="localValue"
          type="text"
          class="field-input"
          :placeholder="label"
          autocomplete="off"
          @keydown="onKeydown"
        />
      </div>

      <div class="modal-actions">
        <button type="button" class="btn btn-secondary btn-sm" @click="clearFilter">
          Limpar
        </button>
        <button type="button" class="btn btn-primary btn-sm" @click="applyFilter">
          Filtrar
        </button>
      </div>
    </div>
  </AnchoredPanel>
</template>

<style scoped>
/* === Mesma linguagem visual de SelectNoteUser.vue === */

.field-filter-container {
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-color);
  width: 200px;
  display: flex;
  flex-direction: column;
}

.input-section {
  padding: 10px 10px 8px;
}

.field-input {
  background-color: #f7f7f7;
  border: 1px solid var(--border-color);
  color: var(--text-dark);
  font-size: 0.82rem;
  border-radius: 8px;
  display: block;
  width: 100%;
  padding: 5px 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  outline: none;
  box-sizing: border-box;
}

.field-input:focus {
  border-color: var(--brand-light);
  box-shadow: var(--focus-ring-field);
}

.modal-actions {
  padding: 8px 10px;
  border-top: 1px solid var(--border-color);
  margin: 2px 10px 0 10px;
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
}

.btn-sm {
  height: 26px;
  padding: 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
}

</style>